import React, { useState, useEffect, useCallback } from 'react';
import PackageDetail from '@paquery-team/lib-package-detail';
import PropTypes from 'prop-types';
import useDetailIntegrations from 'hooks/useDetailIntegrations';
import { useParams, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { Result, notification } from 'antd';
import { ColumnWidthOutlined } from '@ant-design/icons';
import MeasuresView from 'components/shipment/measuresView';
import JsDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import {
  countriesGlobalsSelector,
  packagesStatusesGlobalsSelector,
} from 'redux/globals/selectors';
import { authProfile } from 'redux/auth/selectors';
import useZones from 'redux/zones';
import { zoneItemsSelector } from 'redux/zones/selectors';
import {
  getZoneFromAddress,
  isProfileAdministrator,
} from 'constants/operations';
import { ExportDateFormat } from 'constants/dateFormats';
import {
  PACKAGE,
  isArrivableAtPaQueryPointStatus,
  isCancellableStatus,
  isFinalStatus,
} from 'constants/packages';
import API from 'constants/api';
import { IMAGE_REGEX } from 'constants/defaultValues';
import rest, { getData } from 'util/Api';
import {
  createWebDelivery,
  createWebVisit,
  getDeliveryHistory,
  getVisitHistory,
  keptSignatureUrl,
  podEntries,
  podRecipient,
  waitForNewDelivery,
  waitForVisitEdit,
} from 'util/deliveryEntries';
import { uploadPodImages } from 'util/firebaseStorage';

const withPod = async (packet, history) => {
  const entries = await podEntries(history, packet.signatureImage);
  if (history.length === 0) return { ...packet, entries };
  const recipient = podRecipient(history);
  return {
    ...packet,
    entries,
    shippingScheduleDestination: {
      ...packet.shippingScheduleDestination,
      destinationAclaration: recipient.nombre ?? '',
      destinationDocNumber: recipient.dni ?? '',
      tramitNumber: recipient.tramitNumber ?? '',
    },
  };
};

const disabledInputs = {
  externalCode: true,
  caption: true,
  estimatedCost: true,
  ownerID: true,
  packageType: true,
  deliveryTerm: true,
  arrivedAtPaqueryPointDate: true,
  additionalCode: true,
  shippingScheduleDestination: {
    scheduledDate: true,
    name: true,
    destinationEmail: true,
    shippingAddress: {
      addressDetail: true,
      geoKey: true,
    },
    destinationAclaration: true,
    destinationDocNumber: true,
    tramitNumber: true,
    zone: true,
  },
  shippingScheduleOrigin: {
    name: true,
    phone: true,
    shippingAddress: {
      addressDetail: true,
      geoKey: true,
    },
  },
  storeId: true,
};

const validationSchema = Yup.object().shape({
  status: Yup.number(),
  packageType: Yup.number(),
  reason: Yup.string().when('status', {
    is: 21,
    then: Yup.string()
      .required('Indique el motivo de la cancelación')
      .typeError('Indique el motivo de la cancelación'),
    otherwise: Yup.string().nullable(),
  }),
  shippingScheduleDestination: Yup.object().shape({
    shippingAddress: Yup.object().shape({
      addressDetail: Yup.string().required('Indique la dirección de entrega'),
    }),
    destinationEmail: Yup.string()
      .nullable()
      .email('Ingrese un formato de correo válido.'),
    deliveryNote: Yup.boolean().nullable(),
    paymentOnDelivery: Yup.boolean().nullable(),
    paymentAmount: Yup.number().when('paymentOnDelivery', {
      is: true,
      then: Yup.number()
        .required('Es necesario especificar un monto')
        .typeError('El monto debe ser un numero valido'),
      otherwise: Yup.number().nullable(),
    }),
  }),
  additionalCode: Yup.string().when('packageType', {
    // when package is type flex, additionalCode is required
    is: 5,
    then: Yup.string().required('Es necesario especificar el codigo de venta'),
    otherwise: Yup.string().nullable(),
  }),
});

const fetchPackageByExternalcode = async (externalCode) => {
  try {
    if (externalCode === null || externalCode === '') {
      const error = new Error('El codigo externo no puede ir vacío.');
      return error;
    }
    const searchParams = new URLSearchParams({
      externalCode,
    });
    const {
      data: { content },
      // eslint-disable-next-line import/no-named-as-default-member
    } = await rest.get(
      `${API.packages.getByExternalCode}?${searchParams.toString()}`,
    );
    if (content.length === 0) {
      const error = new Error(
        'El paquete con el codigo externo indicado no existe',
      );
      return error;
    }
    return content[0];
  } catch (error) {
    if (error.message.includes(404)) {
      return new Error('No se ha encontrado el paquete con el id solicitado');
    }
    return error;
  }
};

const fetchPackageById = async (id) => {
  try {
    if (id === null || id === '') {
      const error = new Error('El codigo externo no puede ir vacío.');
      return error;
    }
    const packet = await getData(`${API.packages.getById}${id}`);
    return packet;
  } catch (error) {
    if (error.message.includes(404)) {
      return new Error('No se ha encontrado el paquete con el id solicitado');
    }
    return error;
  }
};

const PackageModify = () => {
  useZones();
  const { externalCode, id } = useParams();
  const history = useHistory();

  const zones = useSelector(zoneItemsSelector);
  const countries = useSelector(countriesGlobalsSelector);
  const profileLogged = useSelector(authProfile);
  const packageStatuses = useSelector(packagesStatusesGlobalsSelector);

  const [marketplaces, setMarketplaces] = useState([]);
  const [stores, setStores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const [packet, setPacket] = useState(null);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [visitHistory, setVisitHistory] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState([]);
  // Pestañas de integraciones (hoy: Interbank), portado desde webapp-opl.
  const extraTabs = useDetailIntegrations(packet);

  const getAllowedStatus = (statusList, currentStatus) => {
    const allowedStatus = [];
    if (isProfileAdministrator(profileLogged)) {
      return statusList;
    }
    const currentStatusFullDescription = statusList.find(
      (status) => status.value === currentStatus,
    );
    allowedStatus.push(currentStatusFullDescription);
    if (currentStatus === PACKAGE.STATUSES.DELIVERYATTEMPTTWO) {
      const visitFirstTryFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.DELIVERYATTEMPTONE,
      );
      allowedStatus.push(visitFirstTryFullDescription);
      const devolutionProcessFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.INRETURNPROCESS,
      );
      allowedStatus.push(devolutionProcessFullDescription);
    }
    if (currentStatus === PACKAGE.STATUSES.DELIVERYATTEMPTONE) {
      const visitSecondTryFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.DELIVERYATTEMPTTWO,
      );
      allowedStatus.push(visitSecondTryFullDescription);
    }
    if (isArrivableAtPaQueryPointStatus(currentStatus)) {
      const arrivedPaquerypointFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.ARRIVEDATPAQUERYPOINT,
      );
      allowedStatus.push(arrivedPaquerypointFullDescription);
    }
    if (currentStatus === PACKAGE.STATUSES.PENDINGENTRYPAQUERYPOINT) {
      const onPaqueryPointFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.LOGGEDINTOPAQUERY,
      );
      allowedStatus.push(onPaqueryPointFullDescription);
    }
    if (isCancellableStatus(currentStatus)) {
      const cancelledFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.CANCELLED,
      );

      allowedStatus.push(cancelledFullDescription);
    }
    if (currentStatus === PACKAGE.STATUSES.CANCELLED) {
      const devolutionProcessFullDescription = statusList.find(
        (status) => status.value === PACKAGE.STATUSES.INRETURNPROCESS,
      );
      allowedStatus.push(devolutionProcessFullDescription);
    }
    return allowedStatus.filter(Boolean);
  };

  const getPacket = useCallback(
    // eslint-disable-next-line consistent-return
    async (fetch, data) => {
      const interfacePackage = async (_packet) => {
        try {
          const marketplace = await getData(
            API.marketplaces.getById(_packet.ownerID),
          );
          setMarketplaces((mkps) => [...mkps, marketplace]);
          // Si el paquete tiene una tienda asignada, fetchear la tienda.
          if (_packet.storeId) {
            const store = await getData(API.stores.getById(_packet.storeId));
            setStores((_stores) => [..._stores, store]);
          }

          _packet.shippingScheduleDestination.zone =
            _packet.shippingScheduleDestination.zone?.id;

          const [history, visits] = await Promise.all([
            getDeliveryHistory(_packet.id),
            getVisitHistory(_packet.id),
          ]);
          setDeliveryHistory(history);
          setVisitHistory(visits);

          return withPod(_packet, history);
        } catch (error) {
          return error;
        }
      };
      const packetResponse = await fetch(data);
      if (packetResponse instanceof Error) {
        setErrors(packetResponse.message);
      } else {
        const convertedPackage = await interfacePackage(packetResponse);
        if (convertedPackage instanceof Error) {
          return setErrors(convertedPackage.message);
        }
        setFilteredStatus(
          getAllowedStatus(packageStatuses, convertedPackage.status),
        );
        setPacket(convertedPackage);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageStatuses, countries],
  );

  const statusesLoaded = packageStatuses?.length > 0;

  useEffect(() => {
    if (externalCode && statusesLoaded) {
      getPacket(fetchPackageByExternalcode, externalCode);
    }
  }, [externalCode, getPacket, statusesLoaded]);

  useEffect(() => {
    if (id && statusesLoaded) {
      getPacket(fetchPackageById, id);
    }
  }, [id, getPacket, statusesLoaded]);

  const unassignPacket = async () => {
    try {
      const payload = {
        id: parseInt(id, 10),
      };
      await rest.put(API.paquer.unassign(id), payload);
    } catch (error) {
      notification.error({
        message: 'Ocurrio un problema',
        description: `Ha ocurrido un error al modificar el paquete${
          error.message ? `: ${error.message}` : '.'
        }`,
      });
    }
  };

  const parsePacket = async (values) => {
    const resolvedZone = await getZoneFromAddress(
      values.shippingScheduleDestination.shippingAddress.addressDetail,
      profileLogged.logisticOperatorID,
    );
    if (!resolvedZone.id)
      notification.error({
        message: 'Oops!',
        description: 'No se ha encontrado una zona para esa dirección.',
      });
    const builtPackage = {
      ...values,
      signatureImage: values.signatureFileName
        ? values.signatureImage.replace(IMAGE_REGEX, '')
        : undefined,
      signatureFileName: values.signatureFileName,
      shippingScheduleOrigin: {
        ...values.shippingScheduleOrigin,
        driver: packet.shippingScheduleOrigin?.driver,
      },
      shippingScheduleDestination: {
        ...values.shippingScheduleDestination,
        zoneID: values.shippingScheduleDestination.zone?.id,
        distributionZoneID:
          values.shippingScheduleDestination.distributionZone?.id,
        driver: packet.shippingScheduleDestination?.driver,
        shippingAddress: {
          ...values.shippingScheduleDestination.shippingAddress,
          lat: resolvedZone.lat,
          lng: resolvedZone.lng,
          postalCode: resolvedZone.postalCode,
          resolutedAddress: resolvedZone.resolutedAddress,
          resolutedStreet: resolvedZone.resolutedStreet,
          resolutedStreetNumber: resolvedZone.resolutedStreetNumber,
          resolutedLocality: resolvedZone.resolutedLocality,
        },
      },
      zone: {
        id: values.shippingScheduleDestination.zone,
      },
    };
    return builtPackage;
  };

  const submitEdition = async (values) => {
    setSaving(true);
    const putPackage = await parsePacket(values);
    const prevStatus = packet.status;
    const nextStatus = putPackage.status;
    const hasDriver =
      putPackage.shippingScheduleDestination.driver ||
      putPackage.shippingScheduleOrigin.driver;

    try {
      const response = await rest.put(
        `${API.packages.get}/${putPackage.id}`,
        putPackage,
      );
      if (rest.isSuccessResponse(response)) {
        notification.success({
          message: 'Modificación de paquete correcto',
          description: `El paquete ${packet.externalCode} ha sido modificado correctamente.`,
        });
        if (
          nextStatus === PACKAGE.STATUSES.ARRIVEDATPAQUERYPOINT &&
          prevStatus !== nextStatus &&
          hasDriver
        ) {
          unassignPacket();
        }
        setTimeout(() => {
          history.goBack();
        }, 1000);
      }
    } catch (error) {
      notification.error({
        message: 'Ocurrio un problema',
        description: `Ha ocurrido un error al modificar el paquete${
          error.message ? `: ${error.message}` : '.'
        }`,
      });
      setSaving(false);
    }
  };

  const handleUpdateDelivery = async (update) => {
    const { files = [], keptUrls = [], signature, ...data } = update;
    try {
      const uploaded = files.length
        ? await uploadPodImages(packet.id, files)
        : [];
      const marked =
        'signature' in update
          ? signature
          : { url: keptSignatureUrl(deliveryHistory, keptUrls) ?? uploaded[0] };
      const signatureUrl =
        marked?.url ??
        (marked?.file ? uploaded[files.indexOf(marked.file)] : undefined);
      const imageUrls = [...keptUrls, ...uploaded].filter(
        (url) => url !== signatureUrl,
      );
      const response = await createWebDelivery(packet.id, {
        ...data,
        signatureUrl,
        imageUrls,
      });
      if (!rest.isSuccessResponse(response))
        throw new Error(response.data?.message);
      const { history, appeared } = await waitForNewDelivery(
        packet.id,
        deliveryHistory.length,
      );
      setDeliveryHistory(history);
      setPacket(await withPod(packet, history));
      if (appeared) {
        notification.success({
          message: 'Entrega registrada',
          description:
            'La entrega quedó registrada. La anterior sigue en el historial.',
        });
      } else {
        notification.info({
          message: 'Entrega en proceso',
          description:
            'El registro se envió y se está procesando. Puede tardar unos segundos en aparecer en el historial.',
        });
      }
    } catch (error) {
      notification.error({
        message: 'No se pudo registrar la entrega',
        description: error.message || 'Ha ocurrido un error en el servidor.',
      });
      throw error;
    }
  };

  const handleUpdateVisit = async ({
    numberVisit,
    submissionId,
    files = [],
    keptUrls = [],
    ...data
  }) => {
    try {
      const uploaded = files.length
        ? await uploadPodImages(packet.id, files)
        : [];
      const response = await createWebVisit(packet.id, {
        ...data,
        editsSubmissionId: submissionId,
        imageUrls: [...keptUrls, ...uploaded],
      });
      if (!rest.isSuccessResponse(response))
        throw new Error(response.data?.message);
      const { visits, appeared } = await waitForVisitEdit(
        packet.id,
        numberVisit,
        submissionId,
      );
      setVisitHistory(visits);
      if (appeared) {
        notification.success({
          message: 'Visita corregida',
          description: `La visita ${numberVisit} quedó corregida. La versión anterior sigue en el historial.`,
        });
      } else {
        notification.info({
          message: 'Corrección en proceso',
          description:
            'La corrección se envió y se está procesando. Puede tardar unos segundos en aparecer.',
        });
      }
    } catch (error) {
      notification.error({
        message: 'No se pudo corregir la visita',
        description: error.message || 'Ha ocurrido un error en el servidor.',
      });
      throw error;
    }
  };

  const handleDownloadRemito = async () => {
    try {
      // eslint-disable-next-line import/no-named-as-default-member
      const response = await rest.get(
        `${API.packages.downloadDeliveryNoteById(packet.id)}`,
        {
          responseType: 'blob',
        },
      );
      if (!rest.isSuccessResponse(response)) {
        // converts blob to text
        const error = await response.data.text();
        throw new Error(error);
      }
      const filename = `Remito ${packet.externalCode} ${dayjs()
        .tz()
        .format(ExportDateFormat)}.pdf`;
      JsDownload(response.data, filename);
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);
      notification.error({
        message: 'Ha ocurrido un error al traer el remito',
        description: `Intente nuevamente por favor o informelo a los administradores${
          exception.message ? ` - ${exception.message}` : '.'
        }`,
      });
    }
  };

  const handleBlur = async (destinationAddressValue, setFieldValue) => {
    if (!destinationAddressValue) return;
    try {
      const zone = await getZoneFromAddress(
        destinationAddressValue,
        profileLogged.logisticOperatorID,
      );
      if (zone.id)
        setFieldValue(
          'shippingScheduleDestination.zone',
          parseInt(zone.id, 10),
        );
      else
        notification.error({
          message: 'Oops!',
          description: 'No se ha encontrado una zona para esa dirección.',
        });
    } catch (error) {
      notification.error({
        message: 'Oops!',
        description: 'Ha ocurrido un error. Intente de nuevo más tarde.',
      });
    }
  };

  const measuresTab = {
    menuTitle: 'Peso y medidas',
    cardTitle: 'Peso y medidas',
    key: 'measures',
    icon: <ColumnWidthOutlined />,
    content: (
      <MeasuresView
        shipmentId={packet?.id}
        packageQuantity={packet?.packageBundles?.length}
      />
    ),
  };

  const inputs = {
    ...disabledInputs,
    shippingScheduleDestination: {
      ...disabledInputs.shippingScheduleDestination,
      destinationEmail: isFinalStatus(packet?.status),
    },
  };

  if (errors) return <Result status="warning" title={errors} />;

  return (
    <PackageDetail
      packageStatuses={filteredStatus}
      marketplaces={marketplaces}
      stores={stores}
      zones={zones}
      saving={saving}
      isModify
      countries={countries}
      loaded={!!packet}
      disabledInputs={inputs}
      initialValues={packet}
      validationSchema={validationSchema}
      onSubmit={submitEdition}
      onDownloadRemito={handleDownloadRemito}
      onBlurDeliveryAddress={handleBlur}
      extraTabs={[...extraTabs, measuresTab]}
      deliveryHistory={deliveryHistory}
      visitHistory={visitHistory}
      onUpdateDelivery={handleUpdateDelivery}
      onUpdateVisit={handleUpdateVisit}
    />
  );
};

PackageModify.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      packet: PropTypes.shape({
        prefix: PropTypes.string,
        country: PropTypes.number,
      }),
    }),
  }).isRequired,
};

export default PackageModify;
